import PropTypes from 'prop-types';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import { withDigitalData } from '../../../utils/DigitalDataContext';
import { withLabels } from '../../../utils/LabelsContext';
import { noop } from '../../../utils/noop';
import { withProduct } from '../../../utils/ProductContext';
import Fomo from '../../Fomo/Fomo';
import Drawer from '../../ui/Drawer/Drawer';
import DrawerTab from '../../ui/DrawerTab/DrawerTab';
import { useSizeButtons } from './hooks/useSizeButtons';
import SizeButton from './SizeButton/SizeButton';
import { SizeButtonStyles } from './SizeButtons.style';
import SizeChartButton from './SizeChartButton';

const SizeButtons = ({
  product,
  appCtx,
  labels,
  sizes,
  selectedSize,
  onSizeChange,
  title,
  isSizeChartHidden,
  digitalData,
  sizeChartData,
  fomo,
  inDeliveryOptions,
}) => {
  const hookData = useSizeButtons({
    product,
    appCtx,
    labels,
    sizes,
    selectedSize,
    onSizeChange,
    digitalData,
    sizeChartData,
    fomo,
    inDeliveryOptions,
  });

  if (!hookData.shouldRender) {
    return null;
  }

  const {
    isDrawerActive,
    isSizeChartLoading,
    displaySizeChartBtnRef,
    sortedSizes,
    activateDrawer,
    handleRenderSizeButton,
    handleDrawerOpen,
    handleSizeChartLoad,
    handleOpenSizeChart,
    sizeChartName,
    sizeChartUrl,
    regionCode,
    guideSymbol,
    isEnhancedSizeSelectorActive,
    isSizeChartDataVisible,
    fomoMessage,
    isActiveTabAssistentePersonal,
    sizeDrawerTitle,
    tabHeadings,
    formattedSizeChartData,
    isFomoBadgeEnabled,
    containerClassName,
    titleContainerClassName,
    sizeTitleClassName,
    sizeOptionsClassName,
    hasDrawer,
  } = hookData;

  const SizeButtonsTable = () =>
    sortedSizes.map(({ value, available }, index) => {
      const buttonProps = handleRenderSizeButton({ value, available, index });

      return (
        <SizeButton
          key={`size-${value}-${available ? 'available' : 'unavailable'}`}
          value={buttonProps.value}
          available={buttonProps.available}
          index={buttonProps.index}
          selectedSize={buttonProps.selectedSize}
          onSizeChange={buttonProps.onSizeChange}
          enhancedSizeSelectorClassName={buttonProps.enhancedSizeSelectorClassName}
        />
      );
    });

  return (
    <div className={containerClassName}>
      <div>
        <div className={titleContainerClassName}>
          <span className={sizeTitleClassName}>{title}</span>
          {isFomoBadgeEnabled && <Fomo text={fomoMessage} />}
          {!isEnhancedSizeSelectorActive && (
            <SizeChartButton
              sizeChartName={sizeChartName}
              isSizeChartHidden={isSizeChartHidden}
              activateDrawer={activateDrawer}
              displaySizeChartBtnRef={displaySizeChartBtnRef}
              guideSymbol={guideSymbol}
              labels={labels}
              isEnhancedSizeSelectorActive={isEnhancedSizeSelectorActive}
            />
          )}
        </div>
        {isSizeChartDataVisible && (
          <div className="size-chart-data" data-testid="size-chart-data">
            {selectedSize && formattedSizeChartData}
          </div>
        )}
      </div>
      <div className={sizeOptionsClassName}>
        <SizeButtonsTable />
      </div>
      {hasDrawer && (
        <Drawer
          toggleState={[isDrawerActive, activateDrawer]}
          heading={sizeDrawerTitle}
          isLoading={isSizeChartLoading}
          onOpen={handleDrawerOpen}
          height="100%"
        >
          <DrawerTab
            onOpenSizeChart={handleOpenSizeChart}
            onLoad={handleSizeChartLoad}
            sizeChartName={sizeChartName}
            sizeChartUrl={sizeChartUrl}
            regionCode={regionCode}
            isActiveTabAssistentePersonal={isActiveTabAssistentePersonal}
            tabHeadings={tabHeadings}
          />
        </Drawer>
      )}
      {isEnhancedSizeSelectorActive && (
        <SizeChartButton
          sizeChartName={sizeChartName}
          isSizeChartHidden={isSizeChartHidden}
          activateDrawer={activateDrawer}
          displaySizeChartBtnRef={displaySizeChartBtnRef}
          guideSymbol={guideSymbol}
          labels={labels}
          isEnhancedSizeSelectorActive={isEnhancedSizeSelectorActive}
        />
      )}
      <style jsx>{SizeButtonStyles}</style>
    </div>
  );
};

SizeButtons.defaultProps = {
  sizes: [],
  onSizeChange: noop,
  title: 'Selection Talla',
  selectedSize: undefined,
  isSizeChartHidden: false,
  sizeChartData: {},
  fomo: {},
  inDeliveryOptions: false,
};

SizeButtons.propTypes = {
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  sizes: PropTypes.arrayOf(PropTypes.object),
  onSizeChange: PropTypes.func,
  title: PropTypes.string,
  selectedSize: PropTypes.string,
  isSizeChartHidden: PropTypes.bool,
  digitalData: PropTypes.object.isRequired,
  sizeChartData: PropTypes.object,
  fomo: {},
  inDeliveryOptions: PropTypes.bool,
};

export default withProduct(withLabels(withApplicationContext(withDigitalData(SizeButtons))));
export { SizeButtons };
